// @ts-nocheck

import React, { useCallback } from 'react';
import styled, { css } from 'styled-components';

import { WinaTVSvg } from '../../assets/svg';
import { PictureInPictureActivate } from '../../assets/svg/picture-in-picture';
import { Target } from '../../client';
import { StreamModes } from '../../constants/picture-in-picture';
import usePictureInPicture from '../../hooks/usePictureInPicture';
import { __ } from '../../i18n/l10n';
import CrossCloseButton from '../../modules/grids/components/buttons/CrossCloseButton';
import theme from '../../styled-components/dark';
import { Button } from '../../styled-components/shared';
import { pixelToEm } from '../../utils/common';
import Switch from '../common/Switch';

const Wrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${theme.TVBar.backgroundColor};
  overflow: hidden;
  width: 100%;

  ${({ $isPipCustomActive }) =>
    !$isPipCustomActive
      ? css`
          border-radius: 0.5em;
          height: 3em;
        `
      : css`
          position: relative;
          top: ${pixelToEm(1)}em;

          &::after {
            content: '';
            position: fixed;
            height: calc(100% - 20px);
            width: 100%;
            z-index: 1;
            bottom: 0;
          }
        `}

  ${Target.__MOBILE__
    ? css`
        ${({ $isPipCustomActive }) =>
          !$isPipCustomActive
            ? css`
                width: calc(100% - 1em);
                margin-left: 0.5em;
                margin-bottom: 0.5em;
              `
            : undefined}
      `
    : undefined}
`;

const Text = styled.span`
  position: relative;
  ${({ $isPipCustomActive }) =>
    $isPipCustomActive
      ? css`
          font-size: ${pixelToEm(13)}em;
          padding: ${pixelToEm(2)}em;
        `
      : css`
          font-size: 1.1em;
        `}

  width: 100%;
  color: ${theme.TVBar.textColor};
  text-align: center;
`;

const WTvLogo = styled(WinaTVSvg)`
  position: absolute;
  left: 0.6em;

  ${({ $isPipCustomActive }) =>
    $isPipCustomActive
      ? css`
          height: 1.5em;
          width: 1.5em;
        `
      : css`
          height: 2.75em;
          top: 0.125em;
          width: 2.75em;
        `}
`;

const BulletLive = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  width: 0.3em;
  height: 100%;
  background-color: ${theme.TVBar.borderColor};
`;

const StyledSwitchSelector = styled(Switch)`
  font-size: 1.3em;
`;

const StyledPipButton = styled(Button)`
  ${({ $isPipCustomActive }) =>
    $isPipCustomActive
      ? css`
          display: none;
        `
      : undefined}

  && {
    background: none;
    box-shadow: none;
    margin-right: ${pixelToEm(5)}em;
    padding: 0;
    width: ${pixelToEm(40)}em;
  }
`;

const StyledPipCloseButton = styled(CrossCloseButton)`
  ${({ $isPipCustomActive }) =>
    $isPipCustomActive
      ? css`
          position: relative;
        `
      : css`
          display: none;
        `}

  height: ${pixelToEm(14)}em;
  margin: unset;
`;

const CtaWrapper = styled.div`
  display: flex;
  position: absolute;
  right: ${pixelToEm(2)}em;
`;

const PictureInPictureActivateIcon = styled(PictureInPictureActivate)`
  width: ${pixelToEm(35)}em;
  margin-top: ${pixelToEm(8)}em;
`;

type TvBarProps = {
  className?: string;
  isWTVEnabled: boolean;
  setShowWinamaxTv: (...params: unknown) => unknown;
  showWTV: boolean;
};

const TvBar = ({ isWTVEnabled, className, showWTV, setShowWinamaxTv }: TvBarProps) => {
  const { closeStream, isPipCustomActive, setPip, type } = usePictureInPicture();
  const isWTVPipActive = Target.__MOBILE__ && isPipCustomActive && type === StreamModes.TV;

  const handleToggleSwitch = useCallback(() => {
    // NB: activate this comment to close the pip stream while it is activated and when we the switch ON the WTV
    // NB2: if the comment is activated, adapt the dependencies
    // if (isPipCustomActive && type === StreamModes.MATCH) {
    //   closeStream();
    // }
    setShowWinamaxTv(!showWTV);
  }, [showWTV, setShowWinamaxTv]);

  const handleClickCloseButton = useCallback(() => {
    setShowWinamaxTv(false);
    closeStream();
  }, [closeStream, setShowWinamaxTv]);

  if (!isWTVEnabled) {
    return null;
  }

  return (
    <Wrap $isPipCustomActive={isWTVPipActive} className={className}>
      <BulletLive />
      <WTvLogo $isPipCustomActive={isWTVPipActive} />
      <Text $isPipCustomActive={isWTVPipActive}>{isWTVPipActive ? 'WinaTV' : __('WinaTV channel')}</Text>
      <CtaWrapper $isPipCustomActive={isWTVPipActive}>
        {!isWTVPipActive && Target.__MOBILE__ && showWTV ? (
          <StyledPipButton onClick={setPip}>
            <PictureInPictureActivateIcon title="picture in picture" />
          </StyledPipButton>
        ) : null}
        <StyledPipCloseButton $isPipCustomActive={isWTVPipActive} onClick={handleClickCloseButton} />
        {!isWTVPipActive ? (
          <StyledSwitchSelector
            title="Wina TV"
            onToggle={handleToggleSwitch}
            theme={theme.Switch.Default}
            toggled={showWTV}
          />
        ) : null}
      </CtaWrapper>
    </Wrap>
  );
};

export default TvBar;
